import { Subject, takeUntil } from "rxjs";
import { FeatureBase } from "./feature-base";
import { KonvaEvent } from "./konva-event-constants";
import Konva from "konva";
import { getComponentFromSource } from "../generics-helper/getComponentFromSource.generic";
import { NotClickableComponent } from "../models/components/commonComponents/NotClickableComponent";
import { ComponentNames } from "../helper/componentNames";
import { EditorObject } from "../models/editor-object.model";

const measurement_group_name = 'measurement-group';
const measurement_group_selector = '.measurement-group';
const group_hitbox_name = "measurement-hit-box";
const group_hitbox_selector = ".measurement-hit-box";
const group_arrow_left_name = "measurement-arrow-left";
const group_arrow_left_selector = ".measurement-arrow-left";
const group_arrow_right_name = "measurement-arrow-right";
const group_arrow_right_selector = ".measurement-arrow-right";
const group_line_final_name = "measurement-line-final";
const group_line_final_selector = ".measurement-line-final";
const group_line_vertical_left_name = "measurement-line-vertical-left";
const group_line_vertical_left_selector = ".measurement-line-vertical-left";
const group_line_vertical_right_name = "measurement-line-vertical-right";
const group_line_vertical_right_selector = ".measurement-line-vertical-right";
const group_text_name = "measurement-text";
const group_text_selector = ".measurement-text";
const vertical_line_offset = 10;
const strokeWidth = 2;
const pointerLength = 10;
const pointerWidth = 10;
const fontSize = 16;
const textYOffset = 8;
const circle_radius = 5;
const guideline_offset = 5;

export class MeasurementFeature extends FeatureBase {
    private mouseDownMeasurementWithSnappingHandler: any;
    private mouseMoveMeasurementWithSnappingHandler: any;
    private mouseUpMeasurementWithSnappingHandler: any;
    private mouseDownFreeDrawingHandler: any;
    private mouseMoveFreeDrawingHandler: any;
    private mouseUpFreeDrawingHandler: any;
    private keyDownHandler: any;
    private keyUphandler: any;
    private transformHandler: any;
    private hitboxClickHandler: any;
    private unsubscribe$: Subject<void> = new Subject<void>();
    private drawingLine = false;
    private enableMeasurementTool: boolean = false;
    private snappingEnabled: boolean = false;
    private freeDrawingEnabled: boolean = false;
    private prevScaleX;
    private prevScaleY;
    private startX;
    private startY;
    private endX;
    private endY;
    private line = null;
    private circle = null;

    public initialize() {
        this.mouseDownMeasurementWithSnappingHandler = this.onMouseDownMeasurementWithSnapping.bind(this);
        this.mouseMoveMeasurementWithSnappingHandler = this.onMouseMoveMeasurementWithSnapping.bind(this);
        this.mouseUpMeasurementWithSnappingHandler = this.onMouseUpMeasurementWithSnapping.bind(this);
        this.mouseDownFreeDrawingHandler = this.onMouseDownFreeDrawing.bind(this);
        this.mouseMoveFreeDrawingHandler = this.onMouseMoveFreeDrawing.bind(this);
        this.mouseUpFreeDrawingHandler = this.onMouseUpFreeDrawing.bind(this);

        this.transformHandler = this.onScaleChange.bind(this);

        this.hitboxClickHandler = this.onHitboxClick.bind(this);

        this.editorCanvasComponent.stage.on(KonvaEvent.mousedown, this.mouseDownMeasurementWithSnappingHandler);
        this.editorCanvasComponent.stage.on(KonvaEvent.mousemove, this.mouseMoveMeasurementWithSnappingHandler);
        this.editorCanvasComponent.stage.on(KonvaEvent.mouseup, this.mouseUpMeasurementWithSnappingHandler);
        this.editorCanvasComponent.stage.on(KonvaEvent.mousedown, this.mouseDownFreeDrawingHandler);
        this.editorCanvasComponent.stage.on(KonvaEvent.mousemove, this.mouseMoveFreeDrawingHandler);
        this.editorCanvasComponent.stage.on(KonvaEvent.mouseup, this.mouseUpFreeDrawingHandler);
        this.editorCanvasComponent.stage.on(KonvaEvent.xChange, this.transformHandler);

        this.addContainerEvents();

        this.editorCanvasComponent.editorService.currentLayer.pipe(takeUntil(this.unsubscribe$)).subscribe(value => {
            if (!value) {
                return;
            }

            let currentLayer = this.getCurrentLayer();

            let editorObjects = this.editorCanvasComponent.editorService.getEditorObjects();
            editorObjects.forEach(object => {
                let notClickable = getComponentFromSource<NotClickableComponent>(ComponentNames.notClickableComponent, object.components);

                if (notClickable === null) {
                    object.listening(false);
                }
            });
            currentLayer.drawHit();
        });

        this.editorCanvasComponent.editorService.measurementFunctionState.pipe(takeUntil(this.unsubscribe$)).subscribe((state: boolean) => {
            this.enableMeasurementTool = state;
        });

        this.editorCanvasComponent.editorService.snappingFunctionState.pipe(takeUntil(this.unsubscribe$)).subscribe((state: boolean) => {
            this.snappingEnabled = state;
        });

        this.editorCanvasComponent.editorService.freeDrawingFunctionState.pipe(takeUntil(this.unsubscribe$)).subscribe((state: boolean) => {
            this.freeDrawingEnabled = state;
        });

        this.prevScaleX = this.editorCanvasComponent.stage.scaleX();
        this.prevScaleY = this.editorCanvasComponent.stage.scaleY();
        this.setListeningStateOfMeasurementGroups(true);
    }

    public dispose() {
        this.editorCanvasComponent.editorService.setSnappingFunctionState(false);
        this.editorCanvasComponent.editorService.setFreeDrawingFunctionState(false);
        this.setListeningStateOfMeasurementGroups(false);
        this.restoreEditorObjectListeners();

        this.unsubscribe$.next();
        this.unsubscribe$.complete();

        this.editorCanvasComponent.stage.off(KonvaEvent.mousedown, this.mouseDownMeasurementWithSnappingHandler);
        this.editorCanvasComponent.stage.off(KonvaEvent.mousemove, this.mouseMoveMeasurementWithSnappingHandler);
        this.editorCanvasComponent.stage.off(KonvaEvent.mouseup, this.mouseUpMeasurementWithSnappingHandler);
        this.editorCanvasComponent.stage.off(KonvaEvent.mousedown, this.mouseDownFreeDrawingHandler);
        this.editorCanvasComponent.stage.off(KonvaEvent.mousemove, this.mouseMoveFreeDrawingHandler);
        this.editorCanvasComponent.stage.off(KonvaEvent.mouseup, this.mouseUpFreeDrawingHandler);
        this.editorCanvasComponent.stage.off(KonvaEvent.transform, this.transformHandler);

        this.removeContainerEvents();
    }

    private setListeningStateOfMeasurementGroups(state: boolean) {
        let currentLayer = this.getCurrentLayer();
        let measurementShapes = currentLayer.find(measurement_group_selector);

        measurementShapes.forEach((group: Konva.Group) => {
            group.find(group_hitbox_selector).forEach((hitbox: Konva.Rect) => {
                hitbox.listening(state);
            });
        });
    }

    private restoreEditorObjectListeners() {
        let currentLayer = this.getCurrentLayer();

        let editorObjects = this.editorCanvasComponent.editorService.getEditorObjects();
        editorObjects.forEach(object => {
            let notClickable = getComponentFromSource<NotClickableComponent>(ComponentNames.notClickableComponent, object.components);

            if (notClickable === null) {
                object.listening(true);
            }
        });
        currentLayer.drawHit();
    }

    private onMouseDownMeasurementWithSnapping(e) {
        if (!this.enableMeasurementTool) {
            return;
        }

        this.drawingLine = true;
        const layer = this.getCurrentLayer();
        const pos = this.getRelativePointerPosition(layer);

        if (this.snappingEnabled) {
            if (!this.startX && !this.startY) {
                const scale = this.editorCanvasComponent.stage.scaleX();
                const strokeWidthScaled = 4 / scale;
                let tmpLine = new Konva.Line({ points: [pos.x, pos.y], strokeWidth: strokeWidthScaled });
                layer.add(tmpLine);
                let snapableobjects = this.editorCanvasComponent.getRelevantObjectsForCollissionDetectionInCurrentFloor(this.editorCanvasComponent.editorService.getEditorObjects());
                let snappingPos = this.snapToEdge(tmpLine, snapableobjects);

                this.startX = snappingPos.x;
                this.startY = snappingPos.y;
                tmpLine.remove();
            }
        } else {
            this.startX = pos.x;
            this.startY = pos.y;
        }

        this.setLine();
        this.setCircle();

        layer.add(this.line);
        layer.add(this.circle);
    }

    private onMouseDownFreeDrawing(e) {
        if (!this.freeDrawingEnabled) {
            return;
        }

        this.drawingLine = true;
        const layer = this.getCurrentLayer();
        const pos = this.getRelativePointerPosition(layer);

        this.startX = pos.x;
        this.startY = pos.y;

        this.setLine();
        this.setCircle();

        layer.add(this.line);
        layer.add(this.circle);
    }

    private onMouseMoveMeasurementWithSnapping(e) {
        if (!this.enableMeasurementTool) {
            return;
        }

        if (this.line == null) {
            if (!this.snappingEnabled) {
                return;
            }
            const layer = this.getCurrentLayer();
            const pos = this.getRelativePointerPosition(layer);
            const scale = this.editorCanvasComponent.stage.scaleX();
            const strokeWidthScaled = 4 / scale;
            let tmpLine = new Konva.Line({ points: [pos.x, pos.y], strokeWidth: strokeWidthScaled });
            layer.add(tmpLine);
            let snapableobjects = this.editorCanvasComponent.getRelevantObjectsForCollissionDetectionInCurrentFloor(this.editorCanvasComponent.editorService.getEditorObjects());
            let snappingPos = this.snapToEdge(tmpLine, snapableobjects);

            if (snappingPos && snappingPos.x && snappingPos.y) {
                this.startX = snappingPos.x;
                this.startY = snappingPos.y;
            }

            tmpLine.remove();
        }
        else {
            const layer = this.getCurrentLayer();
            const pos = this.getRelativePointerPosition(layer);
            let points = this.line.points().slice();
            let x = pos.x;
            let y = pos.y;

            if (this.snappingEnabled) {
                let tmpPoints = points.slice();
                tmpPoints[2] = x;
                tmpPoints[3] = y;
                const scale = this.editorCanvasComponent.stage.scaleX();
                const strokeWidthScaled = 4 / scale;
                let tmpLine = new Konva.Line({ points: tmpPoints, strokeWidth: strokeWidthScaled });
                layer.add(tmpLine);
                let snapableobjects = this.editorCanvasComponent.getRelevantObjectsForCollissionDetectionInCurrentFloor(this.editorCanvasComponent.editorService.getEditorObjects());
                let snappingPos = this.snapToEdge(tmpLine, snapableobjects);

                x = snappingPos.x;
                y = snappingPos.y;
                tmpLine.remove();
            }

            // Restrict to vertical or horizontal lines
            const deltaX = Math.abs(x - this.startX);
            const deltaY = Math.abs(y - this.startY);

            if (deltaX > deltaY) {
                // Horizontal line
                points[2] = x;
                points[3] = this.startY;
            } else {
                // Vertical line
                points[2] = this.startX;
                points[3] = y;
            }

            this.line.points(points);
            layer.batchDraw();
        }
    }

    private onMouseMoveFreeDrawing(e) {
        if (!this.freeDrawingEnabled) {
            return;
        }

        if (this.line == null) {
            return;
        }

        const layer = this.getCurrentLayer();
        const pos = this.getRelativePointerPosition(layer);
        let points = this.line.points().slice();

        points[2] = pos.x;
        points[3] = pos.y;

        this.line.points(points);
        layer.batchDraw();
    }

    private onMouseUpMeasurementWithSnapping(e) {
        if (!this.enableMeasurementTool) {
            return;
        }

        if (!this.drawingLine) {
            return;
        }

        this.drawingLine = false;
        const layer = this.getCurrentLayer();
        const pos = this.getRelativePointerPosition(layer);

        let finalX = pos.x;
        let finalY = pos.y;

        if (this.snappingEnabled) {
            let tmpPoints = this.line.points().slice();
            tmpPoints[2] = pos.x;
            tmpPoints[3] = pos.y;
            const scale = this.editorCanvasComponent.stage.scaleX();
            const strokeWidthScaled = 4 / scale;
            let tmpLine = new Konva.Line({ points: tmpPoints, strokeWidth: strokeWidthScaled });
            layer.add(tmpLine);

            let snapableobjects = this.editorCanvasComponent.getRelevantObjectsForCollissionDetectionInCurrentFloor(this.editorCanvasComponent.editorService.getEditorObjects());
            let snappingPos = this.snapToEdge(tmpLine, snapableobjects);

            finalX = snappingPos.x;
            finalY = snappingPos.y;

            tmpLine.remove();
            this.editorCanvasComponent.destroyGuidelines();
        }

        // Restrict to vertical or horizontal lines
        const deltaX = Math.abs(finalX - this.startX);
        const deltaY = Math.abs(finalY - this.startY);

        if (deltaX > deltaY) {
            // Horizontal line
            this.endX = finalX;
            this.endY = this.startY; // Set endY to startY for horizontal line
        } else {
            // Vertical line
            this.endX = this.startX;
            this.endY = finalY;
        }

        this.commonMouseUpLogic(layer);
    }

    private onMouseUpFreeDrawing(e) {
        if (!this.freeDrawingEnabled) {
            return;
        }

        if (!this.drawingLine) {
            return;
        }

        this.drawingLine = false;
        const layer = this.getCurrentLayer();
        const pos = this.getRelativePointerPosition(layer);

        this.endX = pos.x;
        this.endY = pos.y;

        this.commonMouseUpLogic(layer);
    }

    private onScaleChange() {
        const scaleX = this.editorCanvasComponent.stage.scaleX();
        const scaleY = this.editorCanvasComponent.stage.scaleY();

        if (scaleX !== this.prevScaleX || scaleY !== this.prevScaleY) {
            // Perform necessary actions based on the new scale
            let currentLayer = this.getCurrentLayer();
            const strokeWidthScaled = strokeWidth / scaleX;
            const pointerLengthScaled = pointerLength / scaleX;
            const pointerWidthScaled = pointerWidth / scaleX;
            const fontSizeScaled = fontSize / scaleX;
            const textYOffsetScaled = textYOffset / scaleX;

            currentLayer.find(measurement_group_selector).forEach((group: Konva.Group) => {
                let startX;
                let startY;
                let endX;
                let endY;

                group.find(group_line_final_selector).forEach((line: Konva.Line) => {
                    line.strokeWidth(strokeWidthScaled);
                    const points = line.points();
                    startX = points[0];
                    startY = points[1];
                    endX = points[2];
                    endY = points[3];
                });

                const perpAngle = this.calculatePerpendicularDirection(startX, endX, startY, endY);

                const offsetX = this.calculateOffsetX(perpAngle, scaleX);
                const offsetY = this.calculateOffsetY(perpAngle, scaleX);

                const verticalLineLeftPoints = [startX + offsetX, startY + offsetY, startX - offsetX, startY - offsetY];
                const verticalLineRightPoints = [endX + offsetX, endY + offsetY, endX - offsetX, endY - offsetY];

                const hitboxProperties = this.calculateHitboxProperties(startX, startY, endX, endY, verticalLineLeftPoints, verticalLineRightPoints);

                group.find(group_line_vertical_left_selector).forEach((line: Konva.Line) => {
                    line.strokeWidth(strokeWidthScaled);
                    line.points(verticalLineLeftPoints);
                });

                group.find(group_line_vertical_right_selector).forEach((line: Konva.Line) => {
                    line.strokeWidth(strokeWidthScaled);
                    line.points(verticalLineRightPoints);
                });

                group.find(group_arrow_left_selector).forEach((arrow: Konva.Arrow) => {
                    arrow.strokeWidth(strokeWidthScaled);
                    arrow.pointerLength(pointerLengthScaled);
                    arrow.pointerWidth(pointerWidthScaled);
                });

                group.find(group_arrow_right_selector).forEach((arrow: Konva.Arrow) => {
                    arrow.strokeWidth(strokeWidthScaled);
                    arrow.pointerLength(pointerLengthScaled);
                    arrow.pointerWidth(pointerWidthScaled);
                });

                group.find(group_text_selector).forEach((text: Konva.Text) => {
                    text.fontSize(fontSizeScaled);
                    text.offsetX(text.width() / 2);
                    text.offsetY(text.height() / 2 + textYOffsetScaled);
                });

                group.find(group_hitbox_selector).forEach((hitbox: Konva.Rect) => {
                    hitbox.x(hitboxProperties.x);
                    hitbox.y(hitboxProperties.y);
                    hitbox.width(hitboxProperties.width);
                    hitbox.height(hitboxProperties.height);
                });
            });

            this.prevScaleX = scaleX;
            this.prevScaleY = scaleY;

            currentLayer.batchDraw();
        }
    }

    private commonMouseUpLogic(layer) {
        const startX = this.startX;
        const startY = this.startY;
        const endX = this.endX;
        const endY = this.endY;

        if (startX === endX && startY === endY) {
            // Do not draw if the start and end points are the same
            this.line.destroy();
            this.circle.destroy();
            this.line = null;
            this.circle = null;
            this.startX = undefined;
            this.startY = undefined;
            this.endX = undefined;
            this.endY = undefined;
            return;
        }

        this.circle.destroy();
        this.line.destroy();
        this.line = null;
        this.circle = null;
        this.startX = undefined;
        this.startY = undefined;
        this.endX = undefined;
        this.endY = undefined;

        const scale = this.editorCanvasComponent.stage.scaleX();
        const strokeWidthScaled = strokeWidth / scale;


        let measurementGroup = new Konva.Group({ draggable: false, name: measurement_group_name });
        // Draw the final line

        let finalLine = this.createGroupLine([startX, startY, endX, endY], 'skyblue', strokeWidthScaled, group_line_final_name);
        measurementGroup.add(finalLine);

        // Draw arrows at start and end points

        let arrowStartPoints = [startX, startY, startX + (endX - startX), startY + (endY - startY)];
        let arrowStart = this.createGroupArrow(arrowStartPoints, strokeWidthScaled, scale, group_arrow_left_name);

        measurementGroup.add(arrowStart);

        let arrowEndPoints = [endX, endY, endX - (endX - startX), endY - (endY - startY)];
        let arrowEnd = this.createGroupArrow(arrowEndPoints, strokeWidthScaled, scale, group_arrow_right_name);
        
        measurementGroup.add(arrowEnd);

        // Draw vertical lines at start and end points

        const perpAngle = this.calculatePerpendicularDirection(startX, endX, startY, endY);
        const offsetX = this.calculateOffsetX(perpAngle, scale);
        const offsetY = this.calculateOffsetY(perpAngle, scale);

        let vStartPoints = [startX + offsetX, startY + offsetY, startX - offsetX, startY - offsetY];
        let verticalLineStart = this.createGroupLine(vStartPoints, 'orange', strokeWidthScaled, group_line_vertical_left_name);

        measurementGroup.add(verticalLineStart);

        let vEndPoints = [endX + offsetX, endY + offsetY, endX - offsetX, endY - offsetY];
        let verticalLineEnd = this.createGroupLine(vEndPoints, 'orange', strokeWidthScaled, group_line_vertical_right_name);

        measurementGroup.add(verticalLineEnd);

        let distanceText = this.createMeasurementGroupText(startX, startY, endX, endY, scale);

        measurementGroup.add(distanceText);

        const hitboxProperties = this.calculateHitboxProperties(startX, startY, endX, endY, verticalLineStart.points(), verticalLineEnd.points());
        let groupHitBox = new Konva.Rect({
            x: hitboxProperties.x,
            y: hitboxProperties.y,
            width: hitboxProperties.width,
            height: hitboxProperties.height,
            name: group_hitbox_name,
            rotation: hitboxProperties.rotation
        });

        measurementGroup.add(groupHitBox);

        groupHitBox.on(KonvaEvent.click, this.hitboxClickHandler);

        layer.add(measurementGroup);

        // Render the layer
        layer.draw();
    }

    private createGroupArrow(points, strokewidth, scale, name) {
        const pointerLengthScaled = pointerLength / scale;
        const pointerWidthScaled = pointerWidth / scale;

        return new Konva.Arrow({
            points: points,
            pointerLength: pointerLengthScaled,
            pointerWidth: pointerWidthScaled,
            fill: 'skyblue',
            strokeWidth: strokewidth,
            name: name
        });
    }

    private createGroupLine(points, stroke, strokeWidth, name) {
        return new Konva.Line({
            points: points,
            stroke: stroke,
            strokeWidth: strokeWidth,
            name: name
        });
    }

    private createMeasurementGroupText(startX, startY, endX, endY, scale) {
        const fontSizeScaled = fontSize / scale;
        const textYOffsetScaled = textYOffset / scale;

        // Calculate the angle of the line for the text
        let textAngle = Math.atan2(endY - startY, endX - startX) * (180 / Math.PI);

        // Adjust angle to prevent text from flipping upside down
        if (textAngle > 90 || textAngle < -90) {
            textAngle += 180;
        }

        // Calculate distance
        let distance = Number(Math.sqrt(Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2)).toFixed(0));

        let distanceDisplayText = distance + ' mm';

        let distanceText = new Konva.Text({
            x: ((startX + endX) / 2),
            y: ((startY + endY) / 2),
            text: distanceDisplayText,
            fontSize: fontSizeScaled,
            fontFamily: 'Arial',
            fill: 'black',
            rotation: textAngle,
            name: group_text_name
        });

        distanceText.offsetX(distanceText.width() / 2);
        distanceText.offsetY(distanceText.height() / 2 + textYOffsetScaled);

        return distanceText;
    }

    private onHitboxClick(e) {
        let hitbox = e.target as Konva.Rect;
        let isFilled = hitbox.fill() !== undefined;

        if (isFilled) {
            hitbox.fill(undefined);
        } else {
            hitbox.fill('grey')
            hitbox.opacity(0.15)
        }

        hitbox.getStage().batchDraw();
    }

    private snapToEdge(currentObject, snapableObjects: Array<EditorObject>): { x, y } {
        this.editorCanvasComponent.destroyGuidelines();

        // find possible snapping lines
        let lineGuideStops = this.getLineGuideStops(currentObject, snapableObjects);
        // find snapping points of current object
        let itemBounds = this.getMeasurementEdges(currentObject);

        // now find where can we snap current object
        let guides = this.getGuides(lineGuideStops, itemBounds);

        // do nothing if no snapping
        if (!guides.length) {
            return;
        }

        this.editorCanvasComponent.drawGuides(guides);

        let absPos = this.getRelativePointerPosition(currentObject);

        // now force object position
        guides.forEach((lg) => {
            switch (lg.snap) {
                case 'start': {
                    switch (lg.orientation) {
                        case 'V': {
                            absPos.x = lg.lineGuide + lg.offset;
                            break;
                        }
                        case 'H': {
                            absPos.y = lg.lineGuide + lg.offset;
                            break;
                        }
                    }
                    break;
                }
                case 'end': {
                    switch (lg.orientation) {
                        case 'V': {
                            absPos.x = lg.lineGuide + lg.offset;
                            break;
                        }
                        case 'H': {
                            absPos.y = lg.lineGuide + lg.offset;
                            break;
                        }
                    }
                    break;
                }
            }
        });
        return absPos;
    }

    private getSnappingResults(lineGuideStops, itemBounds) {
        let result = [];

        lineGuideStops.forEach((lineGuide) => {
            itemBounds.forEach((itemBound) => {
                let diff = Math.abs(lineGuide - itemBound.guide);
                // if the distance between guide line and object snap point is close we can consider this for snapping
                if (diff < guideline_offset) {
                    result.push({
                        lineGuide: lineGuide,
                        diff: diff,
                        snap: itemBound.snap,
                        offset: itemBound.offset,
                    });
                }
            });
        });

        return result;
    }

    private findClosestSnappingPoints(verticalResults, horizontalResults) {
        let guides = [];

        // find closest snap
        let minV = verticalResults.sort((a, b) => a.diff - b.diff)[0];
        let minH = horizontalResults.sort((a, b) => a.diff - b.diff)[0];

        if (minV) {
            guides.push({
                lineGuide: minV.lineGuide,
                offset: minV.offset,
                orientation: 'V',
                snap: minV.snap,
            });
        }

        if (minH) {
            guides.push({
                lineGuide: minH.lineGuide,
                offset: minH.offset,
                orientation: 'H',
                snap: minH.snap,
            });
        }

        return guides;
    }

    // find all snapping possibilities
    private getGuides(lineGuideStops, itemBounds) {
        let verticalResults = this.getSnappingResults(lineGuideStops.vertical, itemBounds.vertical);
        let horizontalResults = this.getSnappingResults(lineGuideStops.horizontal, itemBounds.horizontal);

        let guides = this.findClosestSnappingPoints(verticalResults, horizontalResults);

        return guides;
    }

    // were can we snap our objects?
    private getLineGuideStops(skipShape, otherObjects) {
        let vertical = [];
        let horizontal = [];

        otherObjects.forEach((guideItem) => {
            if (guideItem === skipShape) {
                return;
            }
            let guideItemBox = guideItem.getClientRect();

            // and we can snap to all edges of shapes
            vertical.push(guideItemBox.x + guideItemBox.width);
            vertical.push(guideItemBox.x);
            horizontal.push(guideItemBox.y + guideItemBox.height);
            horizontal.push(guideItemBox.y);
        });

        return {
            vertical: vertical,
            horizontal: horizontal
        };
    }

    private setLine() {
        const scale = this.editorCanvasComponent.stage.scaleX();
        const strokeWidthScaled = strokeWidth / scale;

        this.line = new Konva.Line({
            stroke: 'rgb(250, 176, 2)',
            strokeWidth: strokeWidthScaled,
            listening: false,
            points: [this.startX, this.startY, this.startX, this.startY]
        });
    }

    private setCircle() {
        const scale = this.editorCanvasComponent.stage.scaleX();
        const strokeWidthScaled = strokeWidth / scale;
        const radiusScaled = circle_radius / scale;

        this.circle = new Konva.Circle({
            x: this.startX,
            y: this.startY,
            radius: radiusScaled,
            strokeWidth: strokeWidthScaled,
            stroke: 'rgba(250, 176, 2,0.75)',
            fill: 'rgba(135,206,235,0.95)'
        });
    }

    private calculateHitboxProperties(startX, startY, endX, endY, verticalLineStartPoints, verticalLineEndPoints) {
        let x = 0;
        let y = 0;

        if (startX > endX) {
            // Dragging left to right
            x = verticalLineStartPoints[0];
            if (startY > endY) {
                // Dragging top to bottom
                y = verticalLineStartPoints[1];
            } else {
                // Dragging bottom to top
                y = verticalLineStartPoints[3];
            }
        } else {
            // Dragging right to left
            x = verticalLineStartPoints[2];
            if (startY > endY) {
                // Dragging top to bottom
                y = verticalLineStartPoints[1];
            } else {
                // Dragging bottom to top
                y = verticalLineStartPoints[3];
            }
        }

        // Calculate the total width as the distance between the start and end points
        const width = Math.sqrt(
            Math.pow(verticalLineEndPoints[0] - verticalLineStartPoints[0], 2) +
            Math.pow(verticalLineEndPoints[1] - verticalLineStartPoints[1], 2)
        );

        // Calculate the height using the vertical offset
        const height = Math.sqrt(
            Math.pow(verticalLineStartPoints[2] - verticalLineStartPoints[0], 2) +
            Math.pow(verticalLineStartPoints[3] - verticalLineStartPoints[1], 2)
        );

        // Calculate the angle of rotation
        const angle = Math.atan2(endY - startY, endX - startX);

        // Convert angle from radians to degrees
        let rotation = angle * (180 / Math.PI);

        // Ensure correct start points for arbitrary angles
        if (Math.abs(rotation) !== 90 || Math.abs(rotation) !== 180 || Math.abs(rotation) !== 270 || Math.abs(rotation) !== 0) {
            // If the rotation angle is between 90 and 180 degrees
            x = verticalLineStartPoints[2];
            y = verticalLineStartPoints[3];
        }

        let result = { x: x, y: y, width, height, rotation };

        return result;
    }

    private calculatePerpendicularDirection(startX, endX, startY, endY) {
        // Calculate the angle of the line
        const angle = Math.atan2(endY - startY, endX - startX);

        // Calculate perpendicular direction (90 degrees to the main line)
        const perpAngle = angle + Math.PI / 2;
        return perpAngle;
    }

    private calculateOffsetX(perpAngle, scale) {
        return (vertical_line_offset * Math.cos(perpAngle)) / scale;
    }

    private calculateOffsetY(perpAngle, scale) {
        return (vertical_line_offset * Math.sin(perpAngle)) / scale;
    }

    private addContainerEvents() {
        const container = this.editorCanvasComponent.stage.container();
        container.tabIndex = 1;
        container.focus();

        this.keyUphandler = this.handleKeyUpBindings.bind(this);
        this.keyDownHandler = this.handleKeyDownBindings.bind(this);

        container.addEventListener('keydown', this.keyDownHandler);
        container.addEventListener('keyup', this.keyUphandler);
    }

    private removeContainerEvents() {
        const container = this.editorCanvasComponent.stage.container();

        container.removeEventListener('keydown', this.keyDownHandler);
        container.removeEventListener('keyup', this.keyUphandler);
    }

    private handleKeyDownBindings(e: KeyboardEvent) {
        let key = e.keyCode;

        // 46 is del key
        if (key == 46) {
            const layer = this.getCurrentLayer();
            this.getSelectedMeasurementGroups().forEach(g => {
                g.destroy();
            });
            // Render the layer
            layer.draw();
        }

        // 32 is spacebar
        if (key == 32) {
            e.preventDefault();
            this.snappingEnabled = true;
        }

    }

    private handleKeyUpBindings(e: KeyboardEvent) {
        let key = e.keyCode;
        // 32 is spacebar
        if (key == 32) {
            e.preventDefault();
            this.snappingEnabled = false;
        }
    }

    // what points of the object will trigger to snapping?
    // it can be just center of the object
    // but we will enable all edges and center
    private getMeasurementEdges(node) {
        const box = node.getClientRect({});
        const absPos = this.getRelativePointerPosition(node);

        //return result;
        return {
            vertical: [
                //left edge
                {
                    guide: Math.round(box.x),
                    offset: Math.round(absPos.x - box.x),
                    snap: 'start',
                },
                // right edge
                {
                    guide: Math.round(box.x + box.width),
                    offset: Math.round(absPos.x - box.x - box.width),
                    snap: 'end',
                },

            ],
            horizontal: [
                // top edge
                {
                    guide: Math.round(box.y),
                    offset: Math.round(absPos.y - box.y),
                    snap: 'start',
                },
                // bottom edge
                {
                    guide: Math.round(box.y + box.height),
                    offset: Math.round(absPos.y - box.y - box.height),
                    snap: 'end',
                },
            ]
        };
    }

    private getSelectedMeasurementGroups() {
        let currentLayer = this.getCurrentLayer();
        return currentLayer.find(measurement_group_selector).filter((group: Konva.Group) => {
            let hitbox = group.find(group_hitbox_selector)[0] as Konva.Rect;

            let isFilled = hitbox.fill() !== undefined;
            return isFilled;
        });
    }
}